<template>
  <v-autocomplete
    :items="itemsForDropdown"
    outlined
    v-bind="$attrs"
    :rules="rules"
    v-model="selected"
    :search-input.sync="searchInput"
    :filter="hasCustomFilter ? customFilter : undefined"
    @input="$emit('input', $event); onChange()"
    @update="$emit('update', $event)"
    @update:search-input="$emit('updateSearchInput', $event)"
    @click:append-outer="$emit('appendOuterIconClick')"
    @change="searchInput = null"
    @blur="$attrs.multiple !== undefined ? searchInput = null : undefined"
  >
      <template v-slot:selection="{ item, index }" v-if="itemsToShow || properCase">
        <div v-if="itemsToShow">
          <v-chip small v-if="index < itemsToShow">
            <span :class="{'proper-case': properCase}">{{$attrs.itemText ? item[$attrs.itemText] : item}}</span>
          </v-chip>
          <span v-if="index === itemsToShow">
            (+{{ selected.length - itemsToShow }} others)
          </span>
        </div>
        <div v-if="!itemsToShow && properCase">
          <v-chip
            v-if="$attrs.chips !== undefined || $attrs.smallChips !== undefined"
            :small="$attrs.smallChips !== undefined"
            :close="$attrs.deletableChips !== undefined"
            @click:close="removeItem(item)"
            :color="item.chipColor || null">
            <span :class="{'proper-case': properCase}" v-html="$attrs.itemText ? item[$attrs.itemText] : item"></span>
          </v-chip>
          <span :class="{'proper-case': properCase}" v-else>
            {{$attrs.itemText ? item[$attrs.itemText] : item}}<span v-if="$attrs.multiple !== undefined  && index !== selected.length - 1">,&nbsp;</span>
          </span>
        </div>
      </template>
      <template v-slot:prepend-item>
        <div v-if="selectAll">
          <v-list-item @click="toggleSelectAll()">
            <v-list-item-action>
              <v-checkbox
                :indeterminate="someItemsSelected"
                :input-value="allItemsSelected"
                value
                readonly>
              </v-checkbox>
            </v-list-item-action>
            <v-list-item-content>
              Select All
            </v-list-item-content>
          </v-list-item>
        </div>
      </template>
      <template v-slot:item="{ item, attrs }">
        <v-list-item-action v-if="$attrs.multiple !== undefined ">
          <v-icon v-if="item.disabled && item.showEdit">mdi-pencil</v-icon>
          <v-checkbox
            v-else
            v-model="attrs.inputValue"
            hide-details
            class="mt-0"
            readonly>
          </v-checkbox>
        </v-list-item-action>
        <v-list-item-content @click="itemClick(item)">
          <v-list-item-title :class="{'proper-case': properCase}">
            <span v-if="itemDisplayText" v-html="item[itemDisplayText] || itemTitle(item)"></span>
            <span v-else>{{ $attrs.itemText ? itemTitle(item) : item }}</span>
          </v-list-item-title>
          <v-list-item-subtitle v-if="subtitleValue || subtitleLabel">
            <span v-html="item[subtitleValue]"></span> {{ subtitleLabel }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </template>
      <slot v-for="(_, name) in $slots" :name="name" :slot="name" />
  </v-autocomplete>
</template>
            
<script>
export default {
  name: 'EsAutocomplete',
  props: {
    required: Boolean,
    items: Array,
    itemsToShow: Number,
    value: [Array, Object, String, Boolean, Number],
    subtitleValue: String,
    subtitleLabel: String,
    itemDisplayText: String,
    hasCustomFilter: {
      type: Boolean,
      default: false,
    },
    customFilterOptions: {
      type: Array,
      default: () => [],
    },
    sort: {
      type: Boolean,
      default: true,
    },
    properCase: {
      type: Boolean,
      default: true,
    },
    selectAll: {
      type: Boolean,
      default: false,
    }
  },
  data: () => ({
    requiredRule: (v) => !!v || 'Required',
    selected: [],
    itemsForDropdown: [],
    searchInput: null,
  }),
  computed: {
    rules() {
      const rules = [];
      if (this.required) {
        rules.push(this.requiredRule);
      }
      return rules;
    },
    allItemsSelected() {
      return this.selected?.length === this.items?.length;
    },
    someItemsSelected() {
      return this.selected?.length > 0 && !this.allItemsSelected;
    },
  },
  mounted() {
    this.selected = this.value;
    this.onChange();
  },
  watch: {
    value(value) {
      this.selected = value;
      this.onChange();
    },
    items() {
      this.onChange();
    },
  },
  methods: {
    onChange() {
      let selectedItems = typeof this.selected !== 'object' || this.$attrs.multiple !== undefined
        ? typeof this.selected === 'boolean' || typeof this.selected === 'number'
          ? [this.selected]
          : this.selected
        : this.selected?.[this.$attrs.itemValue];
      const formattedItems = this.$attrs.itemText
        ? [...this.items].map((item) => {
          let itemCopy = {...item};
          itemCopy[this.$attrs.itemText] = this.$attrs.itemText.split('.').reduce((o, i) => o[i], item);
          return itemCopy;
        })
        : [...this.items];
      const items = this.sort
        ? this.$attrs.itemText
          ? formattedItems.sort((a, b) => a[this.$attrs.itemText]?.toLowerCase() > b[this.$attrs.itemText]?.toLowerCase() ? 1 : -1)
          : formattedItems.sort((a, b) => a?.toLowerCase() > b?.toLowerCase() ? 1 : -1)
        : formattedItems;
      let selected = items.filter((item) => selectedItems?.includes(this.$attrs.itemValue ? item[this.$attrs.itemValue] : item));
      let notSelected = items.filter((item) => !selectedItems?.includes(this.$attrs.itemValue ? item[this.$attrs.itemValue] : item));
      this.itemsForDropdown = selected.length > 0 ? [...selected, { divider: true }, ...notSelected] : [...notSelected];
    },
    itemTitle(item) {
      const splitItemText = this.$attrs.itemText.split('.').reduce((o, i) => o[i], item);
      return splitItemText;
    },
    customFilter(item, queryText) {
      const filterOptions = this.customFilterOptions.map((option) => {
        if (item && item[option]) {
          return item[option].toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1;
        }
      });
      if (filterOptions.includes(true)) {
        return true;
      } else {
        return false;
      }
    },
    removeItem(item) {
      if (this.$attrs.itemValue) {
        if (this.$attrs.returnObject) {
          this.selected = this.selected.filter((x) => x[this.$attrs.itemValue] !== item[this.$attrs.itemValue]);
        } else {
          this.selected = this.selected.filter((x) => x !== item[this.$attrs.itemValue]);
        }
      } else {
        this.selected = this.selected.filter((x) => x !== item);
      }
      this.$emit('input', this.selected);
      this.onChange()
    },
    toggleSelectAll() {
      this.$nextTick(() => {
        if (this.allItemsSelected) {
          this.selected = [];
          this.$emit('input', this.selected);
        } else {
          if (!this.$attrs.itemValue || this.$attrs.returnObject) {
            this.selected = this.items;
          } else {
            this.selected = this.items.map((item) => item[this.$attrs.itemValue]);
          }
          this.$emit('input', this.selected);
        }
      });
    },
    itemClick(item) {
      if (item.disabled) {
        this.$emit('disabledClick', item);
      }
    },
  }
}
</script>

<style scoped>
.proper-case {
  text-transform: capitalize;
}
>>> .v-list-item--disabled {
  pointer-events: all;
}
</style>